'use client'

import { FC, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import * as Yup from 'yup'

import { Button, Input } from '@/components/commons'
import { ArrowLeftIcon, EyeIcon, EyeNoIcon } from '@/components/icons'
import { yupResolver } from '@hookform/resolvers/yup'

import { AgencyCard } from '../AgencyCard'
import { SlideTransition } from '../SlideTransition'
import { IMultiAgencySelectionPageProps } from './types'

const MultiAgencySelectionPage: FC<IMultiAgencySelectionPageProps> = ({
  multiTenantData,
  isRememberMe,
  onContinue,
  userName: _userName,
  savedLoginUser: _savedLoginUser,
  onBack: _onBack,
  onForgotPassword,
  clearPasswordSignal,
}) => {
  const hasMultipleAgencies = multiTenantData.userInfoByTenant.length > 1
  const [selectedTenant, setSelectedTenant] = useState(
    hasMultipleAgencies ? null : multiTenantData.userInfoByTenant[0],
  )
  const [toggleEye, setToggleEye] = useState(false)

  function handleBack() {
    if (selectedTenant && hasMultipleAgencies) {
      // Se há uma agência selecionada, volta para a listagem
      setSelectedTenant(null)
    } else if (_onBack) {
      // Se não há agência selecionada, volta para a tela anterior
      _onBack()
    }
  }

  // Schema para validação de senha
  const schemaPassword = Yup.object({
    pwd: Yup.string().required('Senha é obrigatória'),
    rememberMe: Yup.boolean().notRequired(),
  })

  type PasswordFormProps = Yup.InferType<typeof schemaPassword>

  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormProps>({
    mode: 'all',
    resolver: yupResolver(schemaPassword),
    defaultValues: {
      pwd: '',
      rememberMe: isRememberMe,
    },
  })

  // Limpa o input de senha quando solicitado (ex: após redefinir a senha)
  useEffect(() => {
    if (clearPasswordSignal) {
      resetField('pwd')
      setToggleEye(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearPasswordSignal])

  const onSubmit: SubmitHandler<PasswordFormProps> = async (data) => {
    const tenantToUse = selectedTenant || multiTenantData.userInfoByTenant[0]
    await onContinue(tenantToUse.tenantId, data.pwd, data.rememberMe ?? false)
  }

  return (
    <form
      className="flex flex-col w-full bg-white gap-8"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <h1 className="prose-headline-h6 text-primary-900 text-center md:text-start">
        {hasMultipleAgencies && !selectedTenant
          ? 'Selecione a agência que deseja acessar:'
          : 'Informe sua senha'}
      </h1>

      <div className="relative min-h-[300px] overflow-hidden">
        {/* Listagem de agências (apenas se múltiplas e nenhuma selecionada) */}
        <SlideTransition
          isVisible={hasMultipleAgencies && !selectedTenant}
          direction="left"
          className="absolute inset-0 w-full flex justify-center items-center"
        >
          <div className="grid grid-cols-1 gap-3 w-full h-full overflow-y-auto">
            {multiTenantData.userInfoByTenant.map((tenant) => (
              <AgencyCard
                key={tenant.tenantId}
                tenant={tenant}
                isSelected={false}
                onClick={() => setSelectedTenant(tenant)}
              />
            ))}
          </div>
        </SlideTransition>

        {/* Card da agência selecionada + Input de senha */}
        <SlideTransition
          isVisible={!!selectedTenant}
          direction="left"
          className="absolute inset-0 w-full flex justify-center items-center"
        >
          <div className="flex flex-col w-full gap-8">
            {/* Card da agência selecionada */}
            {selectedTenant && (
              <div className="flex flex-col w-full gap-2">
                <AgencyCard tenant={selectedTenant} disabled />
              </div>
            )}

            {/* Input de senha */}
            <Input
              {...register('pwd')}
              autoCapitalize="none"
              autoComplete="off"
              label="Senha"
              placeholder="*****"
              type={toggleEye ? 'text' : 'password'}
              iconRight={
                toggleEye ? (
                  <EyeIcon onClick={() => setToggleEye(!toggleEye)} />
                ) : (
                  <EyeNoIcon onClick={() => setToggleEye(!toggleEye)} />
                )
              }
              classNameIconRight="cursor-pointer"
              wFull
              helperText={errors.pwd?.message}
            />

            {/* Link Esqueci minha senha */}
            {selectedTenant && onForgotPassword && (
              <p
                onClick={() => onForgotPassword(selectedTenant.tenantId)}
                className="prose-body-small text-primary-900 hover:underline cursor-pointer self-end"
              >
                Esqueci minha senha
              </p>
            )}
          </div>
        </SlideTransition>
      </div>

      <div className="flex flex-col w-full justify-between gap-4">
        <Button
          text="Continuar"
          type="submit"
          className="w-full"
          disabled={hasMultipleAgencies && !selectedTenant}
          isLoading={isSubmitting}
        />
        <Button
          text="Voltar"
          type="button"
          variant="secondary"
          className="w-full"
          iconLeft={<ArrowLeftIcon />}
          onClick={handleBack}
        />
      </div>
    </form>
  )
}

export default MultiAgencySelectionPage
